import React, { useState, useEffect } from "react";
import { observer } from "mobx-react";
import {
  Modal,
  Stack,
  TextInput,
  Select,
  NumberInput,
  Checkbox,
  Button,
  Text,
  Group,
  Tooltip,
  Textarea,
  Box,
  ActionIcon,
  CopyButton,
  AspectRatio,
  Space,
} from "@mantine/core";

import { Cubby } from "../../stores/Cubby";
import { MODALS } from "../../helpers/constants";
import { TextWithInfo } from "./textWithInfo";
import InfoCircle from "../dataDisplay/infoCircle";
import { QRCodeSVG } from "qrcode.react";
import { convertSatsToFiatString } from "../../helpers/conversions";
import _ from "lodash";
import { IconCheck, IconCopy, IconFileInvoice } from "@tabler/icons-react";
import SatoshisError from "../interactiveData/satoshisInfo";
import SatoshisInfo from "../interactiveData/satoshisInfo";
import { TextWithInfoAndIcon } from "../dataDisplay/textWithInfoAndIcon";
import { ARTICLES } from "../../docs/docsIndex";

const CreateInvoice = observer(({ cubby }: { cubby: Cubby }) => {
  // Local state for form fields
  const [description, setDescription] = useState("");
  const [expiresAt, setExpiresAt] = useState<number | null>(10080); // Default to "7 days"
  const [satoshis, setSatoshis] = useState(0);
  const [isPrivate, setIsPrivate] = useState(false);
  const [blindedRoute, setBlindedRoute] = useState(false);

  // Error states for validation
  const [descriptionError, setDescriptionError] = useState("");
  const [satoshisError, setSatoshisError] = useState("");
  const [isInvoiceDisabled, setIsInvoiceDisabled] = useState(true);
  const [checkboxError, setCheckboxError] = useState("");

  // New state for button disable
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);

  // Function to reset the generated invoice
  const resetGeneratedInvoice = () => {
    cubby.resetUserGeneratedBolt11PayRequest();
  };

  // Set default and disable logic for private and blinded route
  useEffect(() => {
    if (cubby.invoiceShouldDefaultToPrivate) {
      setIsPrivate(true); // default to checked
    } else {
      setIsPrivate(false); // default to unchecked
    }
  }, [cubby.invoiceShouldDefaultToPrivate]);

  useEffect(() => {
    if (isPrivate) {
      setBlindedRoute(false); // Disable blinded route if private is checked
    }
  }, [isPrivate]);

  useEffect(() => {
    resetGeneratedInvoice();
  }, []);

  // Validation logic for description and satoshis
  useEffect(() => {
    const isDescriptionValid = description.length <= 60;
    const isSatoshisValid =
      satoshis >= 0 &&
      (!isPrivate
        ? satoshis <= cubby.inboundLiquidityOfBiggestPublicChannel
        : satoshis <= cubby.inboundLiquidityOfBiggestPrivateChannel);

    // Set errors
    setDescriptionError(isDescriptionValid ? "" : "Max 60 characters");
    setSatoshisError(
      isSatoshisValid
        ? ""
        : `Amount exceeds ${
            isPrivate
              ? cubby.inboundLiquidityOfBiggestPrivateChannel.toLocaleString()
              : cubby.inboundLiquidityOfBiggestPublicChannel.toLocaleString()
          }`
    );

    // Check for validation of checkboxes (either private or blinded route can be checked, not both)
    if (isPrivate && blindedRoute) {
      setCheckboxError("Private and Blinded Route cannot both be checked.");
    } else {
      setCheckboxError("");
    }

    // Disable invoice button if any validation fails
    setIsInvoiceDisabled(
      !isDescriptionValid ||
        !isSatoshisValid ||
        !expiresAt ||
        (isPrivate && blindedRoute)
    );
  }, [
    description,
    expiresAt,
    satoshis,
    isPrivate,
    blindedRoute,
    cubby.inboundLiquidityOfBiggestPrivateChannel,
    cubby.inboundLiquidityOfBiggestPublicChannel,
  ]);

  const handleCreateInvoice = () => {
    setIsButtonDisabled(true);

    // Log all form data
    console.log({
      description,
      expiresAt,
      satoshis,
      isPrivate,
      blindedRoute,
    });
    const thisInvoiceUuid = Math.random().toString(36).substring(2, 10);
    resetGeneratedInvoice();
    cubby.setLastProposedBolt11InvoiceUuid(thisInvoiceUuid);
    cubby.cubbyLife("create_bolt11_user_invoice", {
      description,
      expiresAtInMinutes: expiresAt,
      amountInSatoshis: satoshis,
      isPrivate,
      isBlindedRoute: blindedRoute,
      uuid: thisInvoiceUuid,
    });

    // Re-enable the button after 2000ms
    setTimeout(() => {
      setIsButtonDisabled(false);
    }, 8000);
  };

  // Modify all form field change handlers to reset the generated invoice
  const handleDescriptionChange = (e) => {
    setDescription(e.currentTarget.value);
    resetGeneratedInvoice();
  };

  const handleExpiresAtChange = (value) => {
    setExpiresAt(parseInt(value || "0"));
    resetGeneratedInvoice();
  };

  const handleSatoshisChange = (value) => {
    setSatoshis(Number(value) || 0);
    resetGeneratedInvoice();
  };

  const handleIsPrivateChange = (e) => {
    setIsPrivate(e.currentTarget.checked);
    resetGeneratedInvoice();
  };

  const handleBlindedRouteChange = (e) => {
    setBlindedRoute(e.currentTarget.checked);
    resetGeneratedInvoice();
  };

  // Expiration options mapped to number of minutes
  const expirationOptions = [
    { label: "5 minutes", value: "5" },
    { label: "30 minutes", value: "30" },
    { label: "24 hours", value: "1440" },
    { label: "7 days", value: "10080" }, // Default selection
    { label: "one year", value: "525600" },
  ];

  const maxAmount = isPrivate
    ? cubby.inboundLiquidityOfBiggestPrivateChannel
    : cubby.inboundLiquidityOfBiggestPublicChannel;

  return (
    <>
      <Modal
        opened={cubby.currentModal == MODALS.CREATE_INVOICE}
        onClose={() => {
          cubby.setModal(null);
          resetGeneratedInvoice();
        }}
        title={
          <TextWithInfoAndIcon
            text="Make A Lightning Invoice"
            articleSlug={ARTICLES.RECEIVE_TO_LIGHTNING_INVOICE.slug}
            icon={<IconFileInvoice size={24} />}
            cubby={cubby}
            textSize="lg"
          />
        }
      >
        <Stack>
          {/* Maximum Amount */}
          {/* <Text size="sm">
            maximum: {maxAmount.toLocaleString()} satoshis
            <InfoCircle infoLink="https://megalithic.me" />
          </Text> */}
          {/* <TextWithInfo
            textSize="sm"
            text=
            infoLink="https://docs.megalithic.me"
            infoCircleNudge={{ vertical: 0, horizontal: -4 }}
          /> */}
          <Text size="sm">
            {`Maximum: ${maxAmount.toLocaleString()} satoshis`}
          </Text>
          {/* Satoshis field */}
          <NumberInput
            label="Satoshis"
            // description={<Space h="xs" />}
            value={satoshis}
            onChange={handleSatoshisChange}
            //error={satoshisError}
            // error={
            //   <Text size="xs" c="dimmed">
            //     {" "}
            //     0.00001 BTC / $25.23
            //   </Text>
            // }
            error={satoshisError}
            min={0}
            max={maxAmount}
            required
            disabled={
              cubby.hasOpenPrivateChannels === false &&
              cubby.hasOpenPublicChannels === false
            }
          />
          <SatoshisInfo cubby={cubby} satoshis={satoshis} />

          {/* Expiration field */}
          <Select
            label={"Expires In"}
            placeholder="Select expiration time"
            data={expirationOptions}
            value={expiresAt?.toString()} // Control value
            onChange={handleExpiresAtChange}
          />

          {/* Description field */}
          <TextInput
            label={"Description"}
            value={description}
            onChange={handleDescriptionChange}
            error={descriptionError}
            placeholder="Optional, max 60 characters"
          />

          {/* Private checkbox */}
          <Checkbox
            label={"Private"}
            checked={isPrivate}
            onChange={handleIsPrivateChange}
            disabled={
              (!cubby.hasOpenPublicChannels && cubby.hasOpenPrivateChannels) ||
              (!cubby.hasOpenPrivateChannels && cubby.hasOpenPublicChannels)
            }
          />

          {/* Blinded route checkbox */}
          <Tooltip
            label="Blinded route disabled when private is selected"
            disabled={!isPrivate}
          >
            <Checkbox
              label={"Blinded Route (highly experimental)"}
              checked={blindedRoute}
              onChange={handleBlindedRouteChange}
              disabled={isPrivate}
            />
          </Tooltip>

          {/* Error for checkboxes */}
          {checkboxError && (
            <Text c="red" size="sm">
              {checkboxError}
            </Text>
          )}

          {/* Create invoice button */}
          {!cubby.lastBolt11UserGeneratedPayRequest && (
            <Group justify="center" mt="xs">
              <Button
                leftSection={<IconFileInvoice />}
                onClick={handleCreateInvoice}
                disabled={isInvoiceDisabled || isButtonDisabled}
              >
                Make Invoice
              </Button>
            </Group>
          )}
          {cubby.lastBolt11UserGeneratedPayRequest &&
            cubby.lastBolt11UserGeneratedPayRequest.length > 0 && (
              <Stack gap="xs">
                <AspectRatio ratio={1}>
                  <QRCodeSVG
                    value={cubby.lastBolt11UserGeneratedPayRequest}
                    title={"Lightning Invoice"}
                    size={300}
                    bgColor={"#ffffff"}
                    fgColor={"#000000"}
                    level={"L"}
                    marginSize={6}
                  />
                </AspectRatio>
                <Group justify="center">
                  <Text size="sm" ta="center">
                    {satoshis.toLocaleString()} satoshis (
                    {convertSatsToFiatString(satoshis, cubby)})
                  </Text>

                  <Text size="sm" ta="center">
                    pay with any lightning-enabled Bitcoin wallet
                  </Text>

                  <Group gap="xs" align="center" mb="100px">
                    <Text size="xs">
                      {_.truncate(cubby.lastBolt11UserGeneratedPayRequest, {
                        length: 25,
                      })}
                    </Text>
                    <CopyButton value={cubby.lastBolt11UserGeneratedPayRequest}>
                      {({ copied, copy }) => (
                        <ActionIcon
                          size="lg"
                          variant={copied ? "filled" : "default"}
                          color={copied ? "teal" : "blue"}
                          onClick={copy}
                          aria-label="copy address"
                        >
                          {copied ? <IconCheck /> : <IconCopy />}
                        </ActionIcon>
                      )}
                    </CopyButton>
                  </Group>
                </Group>
              </Stack>
            )}
        </Stack>
      </Modal>
    </>
  );
});

export default CreateInvoice;
